import * as React from 'react';
import { Link } from 'react-router-dom';

import { Celula } from '@/componentes/celula';
import { Button } from '@/componentes/ui/button';
import { Input } from '@/componentes/ui/input';
import { Skeleton } from '@/componentes/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/componentes/ui/table';
import { Valor } from '@/componentes/valor';
import type { Orcamento } from '@/dados';
import { mensagemDoErro, useOrcamentos, useProjetos } from '@/dados';
import { Pagina } from '@/layout/pagina';

/* Histórico — as análises salvas, como estavam no dia em que foram salvas.
 *
 * `entradas` e `resultados` são jsonb OPACOS por decisão de arquitetura: quem
 * salva decide o que registrar, quem lê exibe o que reconhece. Por isso a
 * leitura aqui é tolerante (uma lista de chaves aceitas por grandeza) e cai em
 * "—" quando não reconhece nada. O que NÃO é tolerante é a unidade: percentual
 * só é lido como FRAÇÃO, que é o que o motor produz. Aceitar pontos junto
 * ressuscitaria o erro de 100× que os dois formatadores separados existem para
 * impedir. */

/** Chaves aceitas por grandeza. A primeira reconhecida vence. */
const CHAVES_PRECO = ['ticket', 'preco', 'preco_atual', 'precoAtual'] as const;
const CHAVES_MC_PCT = ['mcPct', 'mc_pct'] as const;
const CHAVES_LUCRO = ['lucroUnit', 'lucro_unit', 'lucroProjeto', 'lucro_projeto'] as const;
const CHAVES_NOME = ['nome_projeto', 'nomeProjeto', 'projeto'] as const;

/** Número de um jsonb opaco. Só `number` finito conta: string aqui é dado sem contrato. */
function lerNumero(
  fonte: Record<string, unknown> | null | undefined,
  chaves: readonly string[],
): number | null {
  if (!fonte) return null;
  for (const chave of chaves) {
    const bruto = fonte[chave];
    if (typeof bruto === 'number' && Number.isFinite(bruto)) return bruto;
  }
  return null;
}

function lerTexto(
  fonte: Record<string, unknown> | null | undefined,
  chaves: readonly string[],
): string | null {
  if (!fonte) return null;
  for (const chave of chaves) {
    const bruto = fonte[chave];
    if (typeof bruto === 'string' && bruto.trim()) return bruto.trim();
  }
  return null;
}

/** Busca sem acento e sem caixa: quem digita "joao" acha "João". */
function chaveDeBusca(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/** Uma linha da tabela, já resolvida. */
type Linha = {
  id: string;
  criadoEm: string;
  projeto: string | null;
  preco: number | null;
  mcPct: number | null;
  lucro: number | null;
};

function montarLinha(orcamento: Orcamento, nomePorProjeto: Map<string, string>): Linha {
  // Nome do projeto: a lista de projetos é a fonte boa (o nome atual, mesmo
  // que tenha sido renomeado). O snapshot é o resgate para orçamento cujo
  // projeto foi removido.
  const doCadastro = orcamento.projeto_id ? nomePorProjeto.get(orcamento.projeto_id) : undefined;
  return {
    id: orcamento.id,
    criadoEm: orcamento.criado_em,
    projeto: doCadastro ?? lerTexto(orcamento.entradas, CHAVES_NOME) ?? null,
    preco: lerNumero(orcamento.resultados, CHAVES_PRECO),
    mcPct: lerNumero(orcamento.resultados, CHAVES_MC_PCT),
    lucro: lerNumero(orcamento.resultados, CHAVES_LUCRO),
  };
}

export function Historico() {
  const orcamentos = useOrcamentos();
  const projetos = useProjetos();
  const [busca, setBusca] = React.useState('');

  const linhas = React.useMemo(() => {
    const nomePorProjeto = new Map<string, string>();
    for (const projeto of projetos.data ?? []) {
      if (projeto.id && projeto.nome_projeto) nomePorProjeto.set(projeto.id, projeto.nome_projeto);
    }
    return (orcamentos.data ?? [])
      .map((orcamento) => montarLinha(orcamento, nomePorProjeto))
      // Mais recente primeiro. `criado_em` é ISO, então a ordem de texto é a
      // ordem de tempo; não construímos Date só para comparar.
      .sort((a, b) => b.criadoEm.localeCompare(a.criadoEm));
  }, [orcamentos.data, projetos.data]);

  const termo = chaveDeBusca(busca.trim());
  const visiveis = termo
    ? linhas.filter((linha) => chaveDeBusca(linha.projeto ?? '').includes(termo))
    : linhas;

  return (
    <Pagina
      titulo="Histórico"
      descricao="Análises salvas, como estavam no dia em que foram salvas."
      acoes={
        <Button asChild variant="outline">
          <Link to="/">Voltar ao painel</Link>
        </Button>
      }
    >
      <div className="grid gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-tinta-2 text-apoio" aria-live="polite">
            {orcamentos.isPending ? 'Carregando' : rotuloContagem(linhas.length, visiveis.length, termo)}
          </p>
          <div className="w-full max-w-campo-longo">
            <label className="sr-only" htmlFor="busca-projeto">
              Buscar por nome do projeto
            </label>
            <Input
              id="busca-projeto"
              type="search"
              value={busca}
              onChange={(evento) => setBusca(evento.target.value)}
              placeholder="Buscar por projeto"
              disabled={orcamentos.isPending || linhas.length === 0}
            />
          </div>
        </div>

        {orcamentos.isError ? (
          <Falha
            titulo="Não foi possível carregar o histórico."
            texto={mensagemDoErro(orcamentos.error)}
            acao={
              <Button variant="outline" size="sm" onClick={() => void orcamentos.refetch()}>
                Tentar de novo
              </Button>
            }
          />
        ) : orcamentos.isPending ? (
          <Carregando />
        ) : linhas.length === 0 ? (
          <Vazio
            titulo="Nenhuma análise salva."
            texto="Use Salvar análise no painel."
            acao={
              <Button asChild size="sm">
                <Link to="/">Ir para o painel</Link>
              </Button>
            }
          />
        ) : visiveis.length === 0 ? (
          <Vazio
            titulo="Nenhuma análise com esse nome de projeto."
            texto="Ajuste o termo da busca ou limpe o campo para ver todas."
            acao={
              <Button variant="outline" size="sm" onClick={() => setBusca('')}>
                Limpar busca
              </Button>
            }
          />
        ) : (
          <Celula respiro="justo">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Data</TableHead>
                  <TableHead scope="col">Projeto</TableHead>
                  <TableHead scope="col" className="text-right">
                    Preço
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Margem de contribuição
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    Lucro por projeto
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visiveis.map((linha) => (
                  <TableRow key={linha.id}>
                    <TableCell className="whitespace-nowrap">
                      <Valor valor={linha.criadoEm} formato="data" tamanho="apoio" />
                    </TableCell>
                    <TableCell className="max-w-campo-longo truncate font-medium">
                      {linha.projeto ?? 'Projeto sem nome'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Valor valor={linha.preco} formato="moeda" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Valor
                        valor={linha.mcPct}
                        formato="percentual"
                        tom={tomPorSinal(linha.mcPct)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Valor valor={linha.lucro} formato="moeda" tom={tomPorSinal(linha.lucro)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Celula>
        )}
      </div>
    </Pagina>
  );
}

/** Ausência não é julgamento: sem valor, o número fica neutro, não vermelho. */
function tomPorSinal(valor: number | null): 'neutro' | 'positivo' | 'negativo' {
  if (valor === null) return 'neutro';
  return valor < 0 ? 'negativo' : 'positivo';
}

function rotuloContagem(total: number, visiveis: number, termo: string): string {
  if (total === 0) return 'Nenhuma análise salva.';
  if (termo) return `${visiveis} de ${total} análises`;
  return total === 1 ? '1 análise salva' : `${total} análises salvas`;
}

function Carregando() {
  return (
    <Celula className="grid gap-3">
      {[0, 1, 2, 3].map((linha) => (
        <Skeleton key={linha} className="h-8 w-full" />
      ))}
    </Celula>
  );
}

/* Falha e Vazio ocupam o mesmo lugar da tela e mesmo assim são dois
 * componentes, porque são dois fatos diferentes:
 *
 *   Falha   deu errado agora, e pede nova tentativa. Campo TELHA, que é o
 *           campo da reprova em todo o produto.
 *   Vazio   ninguém salvou nada ainda, que é o estado normal de quem começou
 *           hoje. Fica em nata: ausência esperada não é perda, e pintá-la de
 *           telha mentiria para o usuário novo.
 *
 * Um componente só para os dois foi o que impediu, até aqui, unificar o campo
 * do erro nas dez telas. */
/** Título, uma linha de contexto e a saída. A forma serve aos dois estados. */
type PropsEstado = { titulo: string; texto: string; acao?: React.ReactNode };

function Falha({ titulo, texto, acao }: PropsEstado) {
  return (
    <Celula
      campo="telha"
      respiro="folgado"
      role="alert"
      className="grid justify-items-center gap-2 py-12 text-center"
    >
      <p className="titulo-bloco">{titulo}</p>
      <p className="apoio">{texto}</p>
      {acao ? <div className="pt-2">{acao}</div> : null}
    </Celula>
  );
}

function Vazio({ titulo, texto, acao }: PropsEstado) {
  return (
    <Celula respiro="folgado" className="grid justify-items-center gap-2 py-12 text-center">
      <p className="titulo-bloco">{titulo}</p>
      <p className="apoio">{texto}</p>
      {acao ? <div className="pt-2">{acao}</div> : null}
    </Celula>
  );
}
